import { doc, onSnapshot } from "firebase/firestore";
import * as Sentry from "@sentry/react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchConfigurations,
  getPlanningDataForMentee,
  getPlanningDataForMentor,
  getSessionsPublicData,
  newEventPropertyBuilder,
  updateFirestore,
} from "../api/planning";
import { analytics } from "../common/analytics";
import { db } from "../lib/firebase";
import {
  setIsAuthorizedMentor,
  setRole,
  setMentorEmail,
  setMenteeEmail,
} from "../state/common/generalConfigsReducer";
import { useAppDispatch, useAppSelector } from "../state/hooks";
import { selectConfigurations } from "../state/planner/configurationsReducer";
import { setTrialDocConfig } from "../state/stepsReducer/stepsReducer";
import { trackAnalytics } from "../../src/api/analytics";
import { useQuery } from "utils/hooks/useQuery";
import {
  setMenteeData,
  setMentorData,
  setSessionId,
} from "state/planner/plannerReducer";
import { setTrialInputAreas } from "state/planner-input-areas-reducer/plannerInputAreasReducer";
interface Props {
  children: React.ReactNode;
}

export const TrialV4Container: React.FC<Props> = ({ children }) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(true);
  const appContainerRef = useRef<any>();
  const configurationsData = useAppSelector(selectConfigurations);
  const pathName = window.location.pathname.split("/");
  let sessionId: any = pathName[pathName.length - 1];
  const initialTrialLaunchTime = useAppSelector(
    (state) => state.generalConfigs.trial_doc_opened_time
  );
  const plannerData = useAppSelector((state) => state.plannerInputAreas);
  const menteeDetails = useAppSelector((state) => state.planner.menteeData);
  const query = new URLSearchParams(window.location.search);
  let role: any = query.get("role") ? query.get("role") : "";
  const navigate = useNavigate();
  const params = useQuery();

  useEffect(() => {
    dispatch(setRole(role));
    const plannerDocRef = doc(db, "plan", sessionId); //TODO docId
    if (configurationsData.status === "idle") {
      dispatch(
        fetchConfigurations({
          config: "areas-of-improvements",
        })
      );
      // step to check if the planner doc id is present in firebase. If not present fetch data from package
      // and create a new object and store into firebase.
      // If doc present, fetch its value from firebase
      try {
        getSessionsPublicData({ session_id: sessionId })
          .then((response) => {
            let menteeId = response?.candidateId;
            let mentorId = response?.mentorId;
            dispatch(setSessionId(sessionId));
            if (
              !response.trialCreatedTime ||
              response.trialCreatedTime === ""
            ) {
              if (params.role === "mentor") {
                localStorage.setItem("mentorCreatedTrialSession", "true");
              }
              updateFirestore("trialCreatedTime", new Date());
            } else {
              localStorage.setItem("mentorCreatedTrialSession", "false");
            }
            getPlanningDataForMentee({ ids: menteeId })
              .then((resMenteeData) => {
                dispatch(setMenteeData(resMenteeData));
                dispatch(setMenteeEmail(resMenteeData?.email));
                getPlanningDataForMentor({ id: mentorId })
                  .then((resMentorData) => {
                    dispatch(setMentorData(resMentorData));
                    dispatch(setMentorEmail(resMentorData?.email));
                    let trialDocVisitedEventBuilder;
                    if (params.role === "mentor") {
                      trialDocVisitedEventBuilder = newEventPropertyBuilder({
                        mentee_email: resMenteeData?.email,
                        mentor_email: resMentorData?.email,
                        user_email: resMentorData?.email,
                        product_name: "Trial Doc",
                        event_name: "Trial Doc Visited",
                        track_details: {
                          mentee_resume_upload_status: resMenteeData.resumeUrl
                            ? "Uploaded"
                            : "Not uploaded",
                        },
                      });
                    } else {
                      trialDocVisitedEventBuilder = newEventPropertyBuilder({
                        mentee_email: resMenteeData?.email,
                        mentor_email: resMentorData?.email,
                        user_email: resMenteeData?.email,
                        product_name: "Trial Doc",
                        event_name: "Trial Doc Visited",
                        track_details: {
                          mentee_resume_upload_status: resMenteeData.resumeUrl
                            ? "Uploaded"
                            : "Not uploaded",
                        },
                      });
                    }
                    if (resMenteeData.resumeUrl) {
                      updateFirestore(
                        "inputAreas.resumeUrl",
                        resMenteeData?.resumeUrl
                      );
                    }
                    trackAnalytics(trialDocVisitedEventBuilder);
                    setLoading(false);
                  })
                  .catch(() => {
                    console.log("Error fetching mentor data");
                  });
              })
              .catch(() => {
                console.log("Error fetching mentee data");
              });
          })
          .catch(() => {
            console.log("Error fetching session data");
          });
      } catch (error) {
        Sentry.captureException(error);
      }
    }
    // dispatching just the change with diff is yet to be completed here
    const unsubscribe = onSnapshot(plannerDocRef, (doc) => {
      let values = doc.data();
      if (
        localStorage.getItem("mentorValidityToken") === "false" &&
        params?.role === "mentor" &&
        params?.recorder === "false"
      ) {
        dispatch(setIsAuthorizedMentor(false));
      }
      if (values) {
        // common structure for older and newer structure
        for (const eachConfigChange in values.config) {
          dispatch(
            setTrialDocConfig({
              key: eachConfigChange,
              value: values?.config[eachConfigChange],
            })
          );
        }

        for (const eachConfigChange in values.inputAreas) {
          dispatch(
            setTrialInputAreas({
              key: eachConfigChange,
              value: values?.inputAreas[eachConfigChange],
            })
          );
        }

        if (values.config && values.config?.routingUrl) {
          navigate(values.config.routingUrl);
        }
      }
      if (localStorage.getItem("mentorCreatedTrialSession") === "false") {
        // if (params.role === "mentor") {
        //   localStorage.setItem("mentorValidityToken", "false");
        // }
      } else {
        localStorage.setItem("mentorCreatedTrialSession", "false");
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  return (
    <div ref={appContainerRef} className="h-full">
      {loading ? (
        <div className="flex items-center bg-[#161616] justify-center w-full h-full">
          {/* TODO: make one common and reusable Loader.tsx component */}
          <div role="status">
            <svg
              aria-hidden="true"
              className="mr-2 w-8 h-8 animate-spin text-gray-600 fill-blue-600"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      ) : (
        children
      )}
    </div>
  );
};
